// RoomType describes the type of a room. Never, never change these field names. They are used throughout
// the system and you risk breaking innumerable things by changing these.
export enum RoomType {
  // MASTER
  MASTER_BED = "MASTER_BED",
  MASTER_BATH = "MASTER_BATH",
  MASTER_CLOSET = "MASTER_CLOSET",
  MASTER_HALL = "MASTER_HALL",
  MASTER_PASS = "MASTER_PASS",
  MASTER_SEAT = "MASTER_SEAT",
  MASTER_SUITE = "MASTER_SUITE",
  MASTER_VESTIBULE = "MASTER_VESTIBULE",
  SHOWER = "SHOWER",
  WATER_CLOSET = "WATER_CLOSET",
  BATH_HALL = "BATH_HALL",
  MASTER_VANITY = "MASTER_VANITY",

  // BEDROOM
  BEDROOM = "BEDROOM",
  BED_PASS = "BED_PASS",
  REACH_CLOSET = "REACH_CLOSET",
  BED_CLOSET = "BED_CLOSET",
  DINING = "DINING",
  GUEST_SUITE = "GUEST_SUITE",
  WALK_IN_CLOSET = "WALK_IN_CLOSET",

  // LIVING
  POWDER = "POWDER",
  FORMAL_DINING = "FORMAL_DINING",
  FORMAL_LIVING = "FORMAL_LIVING",
  KITCHEN = "KITCHEN",
  KITCHEN_CABINETS = "KITCHEN_CABINETS",
  DINING_NOOK = "DINING_NOOK",
  REFRIGERATOR = "REFRIGERATOR",
  KITCHEN_HALL = "KITCHEN_HALL",
  PANTRY = "PANTRY",
  LIVING = "LIVING",
  LIBRARY = "LIBRARY",
  SCREEN = "SCREEN",
  LIVING_HALL = "LIVING_HALL",
  OFFICE = "OFFICE",
  BAY_WINDOW = "BAY_WINDOW",
  COAT_CLOSET = "COAT_CLOSET",

  // BATH
  BATHFULL = "BATHFULL",
  BATHFULL_1_DOOR = "BATHFULL_1_DOOR",
  BATHHALF = "BATHHALF",
  JJ_BATH = "JJ_BATH",
  BED_BATH = "BED_BATH",
  BATHTUB = "BATHTUB",

  // ENTRY
  ENTRY = "ENTRY",
  FOYER = "FOYER",
  ENTRY_BUFFER = "ENTRY_BUFFER",

  // GENERAL
  OPEN_BELOW = "OPEN_BELOW",
  GENERAL = "GENERAL",
  GALLERY = "GALLERY",
  GENERAL_OPEN = "GENERAL_OPEN",
  CLOSET = "CLOSET",
  SOLARIUM = "SOLARIUM",
  READING_NOOK = "READING_NOOK",
  FLEX = "FLEX",
  CABINETS = "CABINETS",

  // CIRCULATION
  HALL = "HALL",
  HALL_NO_WINDOW = "HALL_NO_WINDOW",
  HALL_NO_ENTRY = "HALL_NO_ENTRY",
  STAIRS = "STAIRS",
  STAIRS_EDITOR = "STAIRS_EDITOR",
  CORNER_HALL = "CORNER_HALL",
  HALL_CASED_OPENING = "HALL_CASED_OPENING",

  // UTILITY
  GARAGE = "GARAGE",
  GARAGE_DETACH = "GARAGE_DETACH",
  UTIL_HALL = "UTIL_HALL",
  MECH = "MECH",
  MUDROOM = "MUDROOM",
  DOG = "DOG",
  JUMP = "JUMP",
  LAUNDRY = "LAUNDRY",
  WORKSHOP = "WORKSHOP",
  MUD_LAUNDRY = "MUD_LAUNDRY",

  // OUTDOOR
  FRONT_PORCH = "FRONT_PORCH",
  REAR_PORCH = "REAR_PORCH",
  PATIO = "PATIO",
  WALK = "WALK",
  BREEZEWAY = "BREEZEWAY",
  FIREPLACE = "FIREPLACE",
  FREESTANDING_FIREPLACE = "FREESTANDING_FIREPLACE",
  PORTICO = "PORTICO",
  LANDSCAPE = "LANDSCAPE",

  // TESTING
  NONE = "NONE",
  CHASE = "CHASE",
}

export enum RoomEnclosure {
  CONDITIONED = "CONDITIONED",
  UNCONDITIONED = "UNCONDITIONED",
  EXTERIOR = "EXTERIOR",
  OPEN = "OPEN",
  NONE = "NONE",
}

/** A simplified version of RoomEnclosure that collapses all unconditioned enclosure types into UNCONDITIONED. */
export enum RoomCondition {
  CONDITIONED = "CONDITIONED",
  UNCONDITIONED = "UNCONDITIONED",
}

// the order of these is important - append only or update the classification
// logic in classifyHalfEdge.
// these can also be overriden per room, so changes will require an
// appropriate docMigration.
export enum RoomClass {
  HOUSE,
  BASEMENT,
  GARAGE,
  PORCH,
  DECK,
  PATIO,
  EXTERIOR,
}

// can we deprecate this?
export enum RoomKind {
  INTERIOR,
  EXTERIOR,
  BOUNDARY,
}

// RoomCover represents the relationship of a room with rooms
// from the planar graph above it.
// This is an autogenerated condition and should not be exposed to users
export enum RoomCover {
  COVERED = "COVERED",
  UNCOVERED = "UNCOVERED",
}

// RoomSupport represents the relationship of a room with rooms
// from the planar graph below it
// This is an autogenerated condition and should not be exposed to users
export enum RoomSupport {
  SUPPORTED = "SUPPORTED",
  UNSUPPORTED = "UNSUPPORTED",
}

export enum RoomLabelUnits {
  WHOLE_FEET = "WHOLE_FEET",
  WHOLE_FEET_INCHES = "WHOLE_FEET_INCHES",
}

export enum RoomLabelRounding {
  LOWEST = "LOWEST",
  NEAREST = "NEAREST",
  HIGHEST = "HIGHEST",
}

export enum RoomLabelDimensionType {
  ROOM_DECOMPOSITION = "ROOM_DECOMPOSITION",
  BOUNDING_BOX = "BOUNDING_BOX",
  NONE = "NONE",
  OVERRIDE = "OVERRIDE",
}

/** Controls how room tag dimensions (width/height) are computed from the room outline. */
export enum RoomTagDimensionControl {
  MAXIMAL_RECTANGLE = "MAXIMAL_RECTANGLE",
  AXIS_ALIGNED_BOUNDING_BOX = "AXIS_ALIGNED_BOUNDING_BOX",
}

export const defaultRoomTagDimensionControl = RoomTagDimensionControl.MAXIMAL_RECTANGLE;

/**
 * Room types supported by DDEP (Data Driven Entity Prediction) AI service.
 * This is the canonical list - both agent tools and lib/studio should import from here.
 */
export const DDEP_SUPPORTED_ROOM_TYPES: readonly RoomType[] = [
  RoomType.MASTER_BED,
  RoomType.BEDROOM,
  RoomType.BATHFULL,
  RoomType.BATHHALF,
  RoomType.POWDER,
  RoomType.WATER_CLOSET,
  RoomType.LAUNDRY,
  RoomType.DINING,
  RoomType.DINING_NOOK,
  RoomType.OFFICE,
  RoomType.WALK_IN_CLOSET,
  RoomType.CLOSET,
  RoomType.MASTER_CLOSET,
  RoomType.PANTRY,
  RoomType.GARAGE,
  RoomType.LIVING,
  RoomType.KITCHEN,
];
